import { createStart, createMiddleware } from "@tanstack/react-start";

import { renderErrorPage } from "./lib/error-page";
import { attachSupabaseAuth } from "@/integrations/supabase/auth-attacher";
import { REDIRECTS } from "@/lib/redirects.server";

const CITY_SEGMENTS = new Set(["gurgaon", "gurugram", "noida", "ghaziabad", "haridwar", "agra"]);

const CANONICAL_CITY_TOPICS = new Set([
  "piles", "hemorrhoids", "hernia", "gallbladder", "gallstone", "circumcision", "varicose",
  "lipoma", "gynecomastia", "man-boobs", "fissure", "fistula", "delivery", "normal-delivery",
  "c-section", "hysterectomy", "ovarian-cyst", "knee-replacement", "hip-replacement",
  "joint-replacement", "fracture", "carpal-tunnel", "trigger-finger", "kidney-stone", "pcnl",
  "ureteroscopy", "prostate", "turp", "turbt", "tonsillectomy", "tonsil", "septoplasty",
  "adenoidectomy", "nasal-bone", "mastoidectomy", "tympanoplasty", "cataract", "vitrectomy",
  "d-c", "d-c-dilation-and-curettage", "dilation-and-curettage", "mtp", "miscarriage",
  "abortion", "vaginal-tightening", "vaginoplasty", "labiaplasty", "vaginal-prp",
  "stress-urinary-incontinence", "genital-warts", "hymenoplasty", "hymen-reconstruction",
]);

const CITY_TOPIC_SUFFIXES = [
  "symptoms-causes", "surgery-procedure", "surgery-cost", "treatment", "conditions", "procedure",
  "hospital", "doctors", "doctor", "cost", "surgery-risk", "risk", "zsr", "stapler", "laser",
  "surgery",
] as const;

/** Preserve bespoke city pages with genuinely distinct content. */
function isCuratedCityTopic(city: string, topic: string): boolean {
  if (["gurgaon", "agra", "haridwar", "ghaziabad"].includes(city) && topic === "hernia-surgery-cost") return true;
  if (["agra", "haridwar", "ghaziabad"].includes(city) && topic === "hernia-hospital") return true;
  if (topic === "laser-vaginal-tightening-cost") return true;
  if (topic === "vaginal-tightening-surgery-risk") return true;
  if (topic === "hymenoplasty-surgery-procedure") return true;
  if (
    topic.startsWith("gynecomastia-") &&
    ["symptoms-causes", "conditions", "doctors", "hospital", "cost"].some((suffix) =>
      topic.endsWith(`-${suffix}`),
    )
  ) return true;
  if (
    topic.startsWith("piles-") &&
    ["treatment", "surgery-procedure", "hospital", "doctor", "surgery-cost"].some((suffix) =>
      topic.endsWith(`-${suffix}`),
    )
  ) return ["agra", "gurgaon", "ghaziabad", "haridwar"].includes(city);
  return false;
}

function canonicalizeCityTopic(topic: string): string {
  let base = topic;
  let suffix = "";
  for (const candidate of CITY_TOPIC_SUFFIXES) {
    const ending = `-${candidate}`;
    if (topic.endsWith(ending)) {
      base = topic.slice(0, -ending.length);
      suffix = ending;
      break;
    }
  }

  const canonicalBase = ({
    dnc: "d-c",
    gallbladder: "gallstone",
    turp: "prostate",
    hemorrhoids: "piles",
    sui: "stress-urinary-incontinence",
    incontinence: "stress-urinary-incontinence",
    prp: "vaginal-prp",
    tonsil: "tonsillectomy",
    warts: "genital-warts",
  } as Record<string, string>)[base] ?? base;

  if (!suffix && CANONICAL_CITY_TOPICS.has(canonicalBase)) return `${canonicalBase}-treatment`;
  return `${canonicalBase}${suffix}`;
}

/**
 * Consolidate duplicate city landing-page URLs onto one canonical spelling.
 * Handles common spelling errors plus alternate risk/cost slug variants.
 */
function normalizeCityLandingPath(pathname: string): string | null {
  const trimmed = pathname.replace(/\/+$/, "");
  const parts = trimmed.split("/");
  if (parts.length !== 3) return null;
  const city = parts[1]!.toLowerCase();
  if (!CITY_SEGMENTS.has(city)) return null;

  let topic = parts[2]!.toLowerCase();
  const original = topic;

  topic = topic.replace(/vagainal/g, "vaginal");
  topic = topic.replace(/^(?:gynaecomastia|gynecomestia)(?=-|$)/, "gynecomastia");
  // risk variants → /<city>/vaginal-tightening-surgery-risk
  topic = topic.replace(/^(?:laser-)?vaginal-tightening-risk$/, "vaginal-tightening-surgery-risk");
  // cost variants → /<city>/laser-vaginal-tightening-cost
  topic = topic.replace(
    /^(?:laser-)?vaginal-tightening-(?:surgery-)?cost$/,
    "laser-vaginal-tightening-cost",
  );

  topic = canonicalizeCityTopic(topic);
  const canonicalCity = city === "gurugram" ? "gurgaon" : city;

  // Generic symptom, condition, cost, doctor and hospital permutations reuse
  // the same core content. Consolidate them into one strong treatment page.
  if (!isCuratedCityTopic(canonicalCity, topic)) {
    for (const suffix of CITY_TOPIC_SUFFIXES) {
      if (suffix === "treatment") continue;
      const ending = `-${suffix}`;
      if (!topic.endsWith(ending)) continue;
      const base = topic.slice(0, -ending.length);
      if (CANONICAL_CITY_TOPICS.has(base)) topic = `${base}-treatment`;
      break;
    }
  }

  return topic === original && canonicalCity === city ? null : `/${canonicalCity}/${topic}`;
}

const redirectMiddleware = createMiddleware().server(async ({ request, next }) => {

  const url = new URL(request.url);

  const isGet = request.method === "GET" || request.method === "HEAD";
  const isInternalRequest = url.pathname.toLowerCase().startsWith("/_serverfn/");
  const isPageRequest =
    !url.pathname.startsWith("/api/") &&
    !url.pathname.startsWith("/_") &&
    !isInternalRequest &&
    !url.pathname.slice(1).includes(".");

  // Resolve path normalization and known legacy paths before the host redirect.
  // This lets a www + legacy URL reach its final canonical page in one 301.
  let canonicalPath = url.pathname;
  if (isGet && isPageRequest) {
    canonicalPath = canonicalPath.toLowerCase();
    if (canonicalPath.length > 1) canonicalPath = canonicalPath.replace(/\/+$/, "") || "/";
    canonicalPath = REDIRECTS[canonicalPath] ?? normalizeCityLandingPath(canonicalPath) ?? canonicalPath;
  }

  // Canonical host: force www.healic.in → healic.in (matches sitemap + canonical tags).
  // Only rewrite production hosts — leave preview/lovable/localhost alone.
  if (url.hostname === "www.healic.in" && isGet) {
    url.hostname = "healic.in";
    url.pathname = canonicalPath;
    return new Response(null, {
      status: 301,
      headers: { Location: url.toString() },
    });
  }

  if (url.pathname.startsWith("/lovable/")) {
    return next();
  }

  // Collapse duplicate URL spellings onto one canonical path with a 301:
  // uppercase letters and trailing slashes otherwise serve the same page twice.
  if (isGet && isPageRequest) {
    if (canonicalPath !== url.pathname) {
      return new Response(null, {
        status: 301,
        headers: { Location: canonicalPath + url.search },
      });
    }
  }

  const target = REDIRECTS[url.pathname];
  if (target && isGet) {
    return new Response(null, {
      status: 301,
      headers: { Location: target + url.search },
    });
  }

  const normalized = normalizeCityLandingPath(url.pathname);
  if (normalized && (request.method === "GET" || request.method === "HEAD")) {
    return new Response(null, {
      status: 301,
      headers: { Location: normalized + url.search },
    });
  }

  return next();
});

const errorMiddleware = createMiddleware().server(async ({ next }) => {
  try {
    return await next();
  } catch (error) {
    if (error != null && typeof error === "object" && "statusCode" in error) {
      throw error;
    }
    console.error(error);
    return new Response(renderErrorPage(), {
      status: 500,
      headers: { "content-type": "text/html; charset=utf-8" },
    });
  }
});

export const startInstance = createStart(() => ({
  requestMiddleware: [redirectMiddleware, errorMiddleware],
  functionMiddleware: [attachSupabaseAuth],
}));


